import React, { createContext, useContext, useState, useEffect } from 'react';

const UnitContext = createContext();

export const useUnits = () => {
    const context = useContext(UnitContext);
    if (!context) {
        throw new Error('useUnits must be used within a UnitProvider');
    }
    return context;
};

export const UnitProvider = ({ children }) => {
    const [unitSystem, setUnitSystem] = useState('metric'); // 'metric' (kg) or 'imperial' (lbs)

    // Load preference from localStorage on mount
    useEffect(() => {
        const savedUnit = localStorage.getItem('unitSystem');
        if (savedUnit) {
            setUnitSystem(savedUnit);
        }
    }, []);

    // Save preference to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('unitSystem', unitSystem);
    }, [unitSystem]);

    const unitLabel = unitSystem === 'imperial' ? 'LBS' : 'KG';

    /**
     * Converts KG to current unit system value
     * @param {number} kgValue - Value in KG
     * @returns {number} Value in current unit
     */
    const convertWeight = (kgValue) => {
        if (!kgValue) return 0;
        if (unitSystem === 'imperial') {
            return kgValue * 2.20462;
        }
        return kgValue;
    };

    /**
     * Displays formatted weight with unit label
     * @param {number} kgValue - Value in KG
     * @param {boolean} withLabel - Include unit label
     * @returns {string} Formatted string
     */
    const displayWeight = (kgValue, withLabel = true) => {
        const val = convertWeight(kgValue);
        const formatted = Math.round(val * 10) / 10; // Round to 1 decimal
        return withLabel ? `${formatted} ${unitLabel}` : formatted.toString();
    };

    /**
     * Converts input value (in current unit) to KG for storage
     * @param {number} inputValue - Value in current unit
     * @returns {number} Value in KG
     */
    const toKg = (inputValue) => {
        if (!inputValue) return 0;
        if (unitSystem === 'imperial') {
            return inputValue / 2.20462;
        }
        return inputValue;
    };

    const value = {
        unitSystem,
        setUnitSystem,
        unitLabel,
        convertWeight,
        displayWeight,
        toKg
    };

    return (
        <UnitContext.Provider value={value}>
            {children}
        </UnitContext.Provider>
    );
};
